import { t } from 'i18next';
import { forEach } from 'lodash';

import { BomPartType } from 'bom/enums';
import { IConfirmation } from 'core/types';
import { getPlainTextLength, pascalCase } from 'core/utils';
import { ALTERNATE_PART_NUMBER_MAX_LENGTH } from 'parts/constants';
import { ExternalPartStatusType } from 'parts/enums';
import { PricingHelper } from 'pricing/helpers';
import {
  NOTE_DEFAULT_MAX_LENGTH,
  NOTE_SUPSHIP_MAX_LENGTH,
  PURCHASE_ORDER_NUMBER_MAX_LENGTH,
} from 'quotes/constants';
import { QuoteEntityType, QuotePartNoteType, ValidationType } from 'quotes/enums';
import { QuotePartHelper } from 'quotes/helpers';
import { ICustomerQuote, ICustomerQuotePart } from 'quotes/types';
import {
  getInactiveProjectLinkWarningTexts,
  isOneTimeSpaExpirationDateMissing,
  isOneTimeSpaExpirationDatePast,
  isSelectedSpaSource,
} from 'quotes/utils';
import { Color } from 'theme/enums';

import { AlternateOrderPartValidationWarning, AlternateOrderValidationWarning } from './enums';
import { ISubmitValidationResult, IValidationWarningItem } from './types';

export const CELL_LINE_EDIT_WARNING_PREFIX = 'line_error_edit_';
const CELL_LINE_MARGIN_WARNING_PREFIX = 'line_error_';

const buildLineErrorWarnings = (
  parts: ICustomerQuotePart[],
  quoteEntityType: QuoteEntityType
): IValidationWarningItem[] => {
  const submitOverlappingMessages = new Set<string>([
    t('warningValidationTaxonomy'),
    t('warningValidationVendor'),
    t('warningValidationDisallowedManufacturer'),
    t('warningValidationVendorQuoteNumber'),
    t('warningValidationReplacementCostInfo'),
    t('warningValidationExternalPartStatus'),
    t('warningValidationShippingLocation'),
    t('warningValidationShippingLocationNotSet'),
    t('warningValidationQuantityDecimal'),
    t('warningValidationPartNumberTooLong'),
    t('warningValidationMissingDescription'),
    t('warningValidationMissingCost'),
    t('warningValidationMissingPrice'),
    t('warningValidationMissingShipmentType'),
    t('warningValidationMissingManufacturer'),
    t('warningValidationBuyerNoteTooLong'),
    t('warningValidationPickerNoteTooLong'),
    t('warningValidationSupshipNoteTooLong'),
  ]);

  const lineEditCellMessages = new Set<string>([
    t('warningValidationUom'),
    t('warningValidationExternalUomConversion'),
    t('warningValidationQuantityUom'),
    t('warningValidationMoq'),
    t('warningValidationOrderIncrement'),
  ]);

  const grouped: Record<string, number[]> = {};

  forEach(parts, (part) => {
    const partErrors = QuotePartHelper.validationErrors({
      data: part,
      statuses: Object.values(ValidationType),
      withValues: false,
      filteredByStatuses: false,
      quoteEntityType,
    });

    if (!partErrors?.length) {
      return;
    }

    partErrors.forEach((message) => {
      if (submitOverlappingMessages.has(message)) {
        return;
      }
      if (!grouped[message]) {
        grouped[message] = [];
      }
      grouped[message].push(part.line);
    });
  });

  return Object.entries(grouped).map(([message, lines]) => {
    const prefix = lineEditCellMessages.has(message)
      ? CELL_LINE_EDIT_WARNING_PREFIX
      : CELL_LINE_MARGIN_WARNING_PREFIX;

    return {
      id: `${prefix}${message}`,
      warning: t('warningOrderPartValidationOnLinesPrefix', { error: message }),
      lines,
    };
  });
};

export const getFooterValidationWarnings = (
  quote: ICustomerQuote,
  parts: ICustomerQuotePart[],
  quoteEntityType: QuoteEntityType
): ISubmitValidationResult | null => {
  const submitWarnings = getSubmitValidationWarnings(quote, parts);
  const cellLineWarnings = buildLineErrorWarnings(parts, quoteEntityType);

  if (!submitWarnings && cellLineWarnings.length === 0) {
    return null;
  }

  const orderWarnings = submitWarnings?.orderWarnings ?? [];
  const submitLineWarnings = submitWarnings?.lineWarnings ?? [];
  const existingIds = new Set(submitLineWarnings.map((item) => item.id));
  const mergedLineWarnings = [
    ...submitLineWarnings,
    ...cellLineWarnings.filter((item) => !existingIds.has(item.id)),
  ];

  return {
    orderWarnings,
    lineWarnings: mergedLineWarnings,
    count: orderWarnings.length + mergedLineWarnings.length,
  };
};

export const getTotalAmountWarning = (
  minimumOrderThreshold: number,
  parts: ICustomerQuotePart[]
) => {
  if (!minimumOrderThreshold) {
    return false;
  }

  const totalAmountSelected = parts?.reduce(
    (total, part: ICustomerQuotePart) => total + part.extendedPrice,
    0
  );

  return totalAmountSelected < minimumOrderThreshold;
};

export const getSubmitValidationWarnings = (
  quote: ICustomerQuote,
  parts: ICustomerQuotePart[]
): ISubmitValidationResult | null => {
  const isAlternateVersionEnabled = Boolean(quote.alternateVersionEnabled);

  if (!isAlternateVersionEnabled) {
    return null;
  }

  const isArNumberMissing = !quote.billToAddress?.accountsReceivableId;
  const isIsrEmployeeMissing = !quote.isr?.employeeId && !quote.isr?.default;
  const isOsrEmployeeMissing = !quote.osr?.employeeId && !quote.osr?.default;
  const isCustomerNoteExceedingLimit =
    getPlainTextLength(quote.notesMap?.customer?.content) > NOTE_DEFAULT_MAX_LENGTH;
  const isPurchaseOrderNumberTooLong =
    (quote.purchaseOrderNumber?.length ?? 0) > PURCHASE_ORDER_NUMBER_MAX_LENGTH;
  const inactiveProjectLinkWarnings: AlternateOrderValidationWarning[] = [
    ...(quote.inactiveBillToProjectLink
      ? [AlternateOrderValidationWarning.INACTIVE_BILL_TO_PROJECT_LINK]
      : []),
    ...(quote.inactiveShipToProjectLink
      ? [AlternateOrderValidationWarning.INACTIVE_SHIP_TO_PROJECT_LINK]
      : []),
  ];

  const warnings: Record<AlternateOrderPartValidationWarning, number[]> = {
    [AlternateOrderPartValidationWarning.INVALID_QUANTITY_DECIMAL]: [],
    [AlternateOrderPartValidationWarning.PART_NUMBER_TOO_LONG]: [],
    [AlternateOrderPartValidationWarning.MISSING_DESCRIPTION]: [],
    [AlternateOrderPartValidationWarning.MISSING_MANUFACTURER]: [],
    [AlternateOrderPartValidationWarning.MISSING_COST]: [],
    [AlternateOrderPartValidationWarning.MISSING_PRICE]: [],
    [AlternateOrderPartValidationWarning.MISSING_SHIPPING_LOCATION]: [],
    [AlternateOrderPartValidationWarning.MISSING_SHIPMENT_TYPE]: [],
    [AlternateOrderPartValidationWarning.MISSING_VENDOR]: [],
    [AlternateOrderPartValidationWarning.MISSING_VENDOR_QUOTE_NUMBER]: [],
    [AlternateOrderPartValidationWarning.MISSING_TAXONOMY]: [],
    [AlternateOrderPartValidationWarning.DISALLOWED_MANUFACTURER]: [],
    [AlternateOrderPartValidationWarning.EXTERNAL_PART_STATUS_ABANDONED]: [],
    [AlternateOrderPartValidationWarning.MISSING_REPLACEMENT_COST]: [],
    [AlternateOrderPartValidationWarning.MISSING_REPLACEMENT_COST_NUMBER]: [],
    [AlternateOrderPartValidationWarning.MISSING_ONE_TIME_SPA_VALID_UNTIL]: [],
    [AlternateOrderPartValidationWarning.PAST_ONE_TIME_SPA_VALID_UNTIL]: [],
    [AlternateOrderPartValidationWarning.WRONG_SHIPPING_LOCATION_TYPE]: [],
    [AlternateOrderPartValidationWarning.NOTE_BUYER_TOO_LONG]: [],
    [AlternateOrderPartValidationWarning.NOTE_PICKER_TOO_LONG]: [],
    [AlternateOrderPartValidationWarning.NOTE_SUPSHIP_TOO_LONG]: [],
  };

  forEach(parts, (part: ICustomerQuotePart) => {
    if (BomPartType.COMPONENT === part.type) {
      return;
    }

    if (part.quantity !== null && part.quantity !== undefined && !Number.isInteger(part.quantity)) {
      warnings[AlternateOrderPartValidationWarning.INVALID_QUANTITY_DECIMAL].push(part.line);
    }

    if (part.partArr?.number && part.partArr.number.length > ALTERNATE_PART_NUMBER_MAX_LENGTH) {
      warnings[AlternateOrderPartValidationWarning.PART_NUMBER_TOO_LONG].push(part.line);
    }

    if (!part.partArr?.description && !part.partArr?.descriptionLong) {
      warnings[AlternateOrderPartValidationWarning.MISSING_DESCRIPTION].push(part.line);
    }

    if (!QuotePartHelper.isCostPresent(part)) {
      warnings[AlternateOrderPartValidationWarning.MISSING_COST].push(part.line);
    }

    if (!QuotePartHelper.isPricePresent(part)) {
      warnings[AlternateOrderPartValidationWarning.MISSING_PRICE].push(part.line);
    }

    if (!part.corporateLocationId) {
      warnings[AlternateOrderPartValidationWarning.MISSING_SHIPPING_LOCATION].push(part.line);
    }

    if (!part.shipmentTypeId) {
      warnings[AlternateOrderPartValidationWarning.MISSING_SHIPMENT_TYPE].push(part.line);
    }

    if (!part.accountsPayableId) {
      warnings[AlternateOrderPartValidationWarning.MISSING_VENDOR].push(part.line);
    }

    if (!part.taxonomyGroupId) {
      warnings[AlternateOrderPartValidationWarning.MISSING_TAXONOMY].push(part.line);
    }

    if (!QuotePartHelper.isVendorQuoteNumberValid(part)) {
      warnings[AlternateOrderPartValidationWarning.MISSING_VENDOR_QUOTE_NUMBER].push(part.line);
    }

    if (!part.partArr?.manufacturerId) {
      warnings[AlternateOrderPartValidationWarning.MISSING_MANUFACTURER].push(part.line);
    }

    if (part.partArr?.manufacturerId && !part.alternateManufacturerName) {
      warnings[AlternateOrderPartValidationWarning.DISALLOWED_MANUFACTURER].push(part.line);
    }

    if (part.externalPartStatus === ExternalPartStatusType.ABANDONED) {
      warnings[AlternateOrderPartValidationWarning.EXTERNAL_PART_STATUS_ABANDONED].push(part.line);
    }

    // As the BE SubmitOrderValidator: a picked agreement needs no replacement cost nor its number.
    const isSelectedSpaLine = isSelectedSpaSource(part.costSourceType);

    if (
      !isSelectedSpaLine &&
      QuotePartHelper.allowOverrideReplacementCost({
        isAlternateVersionEnabled,
        isShipAndDebitVendor: part.supplier?.isShipAndDebit,
        shipmentTypeCode: part.shipmentType?.code,
      }) &&
      QuotePartHelper.requiredReplacementCost({
        isAlternateVersionEnabled,
        isShipAndDebitVendor: part.supplier?.isShipAndDebit,
        purchaseCost: part.computedPurchaseCost,
      }) &&
      !(part.replacementCost || part.externalReplacementCost)
    ) {
      warnings[AlternateOrderPartValidationWarning.MISSING_REPLACEMENT_COST].push(part.line);
    }

    if (
      !isSelectedSpaLine &&
      QuotePartHelper.requiredReplacementCostNumber({
        isAlternateVersionEnabled,
        isShipAndDebitVendor: part.supplier?.isShipAndDebit,
        purchaseCost: part.computedPurchaseCost,
        replacementCost: part.replacementCost,
      }) &&
      !part.replacementCostNumber
    ) {
      warnings[AlternateOrderPartValidationWarning.MISSING_REPLACEMENT_COST_NUMBER].push(part.line);
    }

    // As the BE SubmitOrderValidator: a one-time SPA line is not sent without Valid until.
    if (isOneTimeSpaExpirationDateMissing(part)) {
      warnings[AlternateOrderPartValidationWarning.MISSING_ONE_TIME_SPA_VALID_UNTIL].push(
        part.line
      );
    }

    if (isOneTimeSpaExpirationDatePast(part)) {
      warnings[AlternateOrderPartValidationWarning.PAST_ONE_TIME_SPA_VALID_UNTIL].push(part.line);
    }

    if (!!part.corporateLocation?.id && !+part.corporateLocation.isAlternateShipping) {
      warnings[AlternateOrderPartValidationWarning.WRONG_SHIPPING_LOCATION_TYPE].push(part.line);
    }

    if (part.notes) {
      const notesArray = Object.values(part.notes);

      const buyerNotes = notesArray.filter((note) => QuotePartNoteType.BUYER === note.type);
      const pickerNotes = notesArray.filter((note) => QuotePartNoteType.PICKER === note.type);
      const supshipNotes = notesArray.filter((note) => QuotePartNoteType.SUPSHIP === note.type);

      const isBuyerNoteExceedingLimit = buyerNotes.some(
        (note) => getPlainTextLength(note.content) > NOTE_DEFAULT_MAX_LENGTH
      );
      const isPickerNoteExceedingLimit = pickerNotes.some(
        (note) => getPlainTextLength(note.content) > NOTE_DEFAULT_MAX_LENGTH
      );
      const isSupshipNoteExceedingLimit = supshipNotes.some(
        (note) => getPlainTextLength(note.content) > NOTE_SUPSHIP_MAX_LENGTH
      );

      if (isBuyerNoteExceedingLimit) {
        warnings[AlternateOrderPartValidationWarning.NOTE_BUYER_TOO_LONG].push(part.line);
      }

      if (isPickerNoteExceedingLimit) {
        warnings[AlternateOrderPartValidationWarning.NOTE_PICKER_TOO_LONG].push(part.line);
      }

      if (isSupshipNoteExceedingLimit) {
        warnings[AlternateOrderPartValidationWarning.NOTE_SUPSHIP_TOO_LONG].push(part.line);
      }
    }
  });

  const errors = Object.fromEntries(
    Object.entries(warnings).filter(([, value]) => value.length > 0)
  ) as Record<AlternateOrderPartValidationWarning, number[]>;

  if (
    Object.keys(errors).length === 0 &&
    !isArNumberMissing &&
    !isIsrEmployeeMissing &&
    !isOsrEmployeeMissing &&
    !isCustomerNoteExceedingLimit &&
    !isPurchaseOrderNumberTooLong &&
    inactiveProjectLinkWarnings.length === 0
  ) {
    return null;
  }

  const orderWarnings: IValidationWarningItem[] = [];
  const lineWarnings: IValidationWarningItem[] = [];

  if (isArNumberMissing) {
    orderWarnings.push({
      id: AlternateOrderValidationWarning.MISSING_AR_NUMBER,
      warning: t('warningOrderPartValidationNoArNumber'),
      action: t('warningOrderPartValidationNoArNumberAction'),
      description: t('warningOrderPartValidationNoArNumberDescription'),
    });
  }

  inactiveProjectLinkWarnings.forEach((id) => {
    orderWarnings.push({ id, ...getInactiveProjectLinkWarningTexts(id) });
  });

  if (isIsrEmployeeMissing) {
    orderWarnings.push({
      id: AlternateOrderValidationWarning.MISSING_ISR_EMPLOYEE,
      warning: t('warningOrderPartValidationNoIsrEmployee'),
      action: t('warningOrderPartValidationNoIsrEmployeeAction'),
    });
  }

  if (isOsrEmployeeMissing) {
    orderWarnings.push({
      id: AlternateOrderValidationWarning.MISSING_OSR_EMPLOYEE,
      warning: t('warningOrderPartValidationNoOsrEmployee'),
      action: t('warningOrderPartValidationNoOsrEmployeeAction'),
    });
  }

  if (isCustomerNoteExceedingLimit) {
    orderWarnings.push({
      id: AlternateOrderValidationWarning.CUSTOMER_NOTE_TOO_LONG,
      warning: t('warningCustomerNoteTooLong'),
      action: t('warningCustomerNoteTooLongAction'),
    });
  }

  if (isPurchaseOrderNumberTooLong) {
    orderWarnings.push({
      id: AlternateOrderValidationWarning.PURCHASE_ORDER_NUMBER_TOO_LONG,
      warning: t('warningPurchaseOrderNumberTooLong', { max: PURCHASE_ORDER_NUMBER_MAX_LENGTH }),
      action: t('warningPurchaseOrderNumberTooLongAction'),
    });
  }

  const errorMessageOptions: Partial<Record<AlternateOrderPartValidationWarning, object>> = {
    [AlternateOrderPartValidationWarning.PART_NUMBER_TOO_LONG]: {
      max: ALTERNATE_PART_NUMBER_MAX_LENGTH,
    },
  };

  forEach(errors, (lines, error) => {
    lineWarnings.push({
      id: error,
      warning: t('warningOrderPartValidationOnLinesPrefix', {
        error: t(`warningOrderPartValidation${pascalCase(error)}`, errorMessageOptions[error]),
      }),
      lines,
    });
  });

  return {
    orderWarnings,
    lineWarnings,
    count: orderWarnings.length + lineWarnings.length,
  };
};

export const getMinimumAmountWarning = (
  minimumOrderThreshold: number,
  parts: ICustomerQuotePart[],
  onConfirm: () => void,
  onCancel: () => void
): IConfirmation | null => {
  if (!getTotalAmountWarning(minimumOrderThreshold, parts)) {
    return null;
  }

  return {
    title: t('titleWarning'),
    message: t('messageConfirmSubmitOrderTotalAmountBelowMinimum', {
      minimum: PricingHelper.formatPricing({ amount: minimumOrderThreshold }),
    }),
    confirmBtnColor: Color.SUCCESS,
    onConfirm,
    onCancel,
  };
};

export const getMissingPartNumberWarning = (
  parts: ICustomerQuotePart[],
  onConfirm: () => void,
  onCancel: () => void
): IConfirmation | null => {
  const quotePartsWithoutPartNumber = parts?.filter(
    (part: ICustomerQuotePart) => !part.partArr?.id
  );

  if (quotePartsWithoutPartNumber?.length === 0) {
    return null;
  }

  return {
    title: t('titleWarning'),
    message: t('messageConfirmSubmitOrderLinesWithoutPartNumberAssigned', {
      linesWithoutPartNumber: quotePartsWithoutPartNumber
        .map((part: ICustomerQuotePart) => part.line)
        .join(', '),
    }),
    color: Color.WARNING,
    confirmBtnColor: Color.SUCCESS,
    onConfirm,
    onCancel,
  };
};

export const getMissingPartNumberAndManufacturerWarning = (
  parts: ICustomerQuotePart[],
  onConfirm: () => void,
  onCancel: () => void
): IConfirmation | null => {
  const quotePartsWithoutPartNumberAndManufacturer = parts?.filter(
    (part: ICustomerQuotePart) => !part.partArr?.id || !part.partArr?.manufacturerId
  );

  if (quotePartsWithoutPartNumberAndManufacturer?.length === 0) {
    return null;
  }

  return {
    title: t('titleWarning'),
    message: t('messageConfirmSubmitOrderLinesWithoutPartNumberAndManufacturerAssigned', {
      linesWithoutPartNumberAndManufacturer: quotePartsWithoutPartNumberAndManufacturer
        .map((part: ICustomerQuotePart) => part.line)
        .join(', '),
    }),
    color: Color.WARNING,
    confirmBtnColor: Color.SUCCESS,
    onConfirm,
    onCancel,
  };
};
